/**
 * Dashboard Stats Cards Component
 * Phase 6 Batch 3 - Core 4 Reports
 * Phase 9.4d - MED-01 (tooltips) & MED-02 (clickable navigation)
 *
 * Displays key business metrics in a grid of stat cards
 * Each card shows a tooltip explaining the metric and navigates to filtered views on click
 */
import { useDashboardStats } from '@/hooks/useDashboardReports';
import { DollarSign, TrendingUp, Briefcase, FileText, AlertCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface StatCard {
  id: string;
  title: string;
  value: string | number;
  icon: typeof DollarSign;
  color: string;
  bgColor: string;
  subtitle?: string;
  tooltip: string;
  navigateTo: string;
}

export function DashboardStatsCards() {
  const { data, isLoading, error } = useDashboardStats();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load dashboard stats
      </div>
    );
  }

  const stats = data?.stats || {};

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Phase 10a.9: Show consulting fees as main number, pass-throughs as subtext
  const ytdFees = stats.ytd_fee_revenue ?? stats.ytd_revenue ?? 0;
  const ytdPassthrough = stats.ytd_passthrough ?? 0;
  const outstandingFees = stats.outstanding_fees ?? stats.outstanding_amount ?? 0;
  const outstandingPassthrough = stats.outstanding_passthrough ?? 0;

  const statCards: StatCard[] = [
    {
      id: 'ytd-revenue',
      title: 'YTD Revenue',
      value: formatCurrency(ytdFees),
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      subtitle: ytdPassthrough > 0
        ? `+ ${formatCurrency(ytdPassthrough)} pass-throughs`
        : `This month: ${formatCurrency(stats.this_month_revenue || 0)}`,
      tooltip: 'Consulting fees (professional, additional services, renewals) from fiscal year start to today. Pass-through expenses shown separately below.',
      navigateTo: '/reports?tab=drilldowns&view=ytd-revenue-by-project',
    },
    {
      id: 'outstanding',
      title: 'Outstanding',
      value: formatCurrency(outstandingFees),
      icon: TrendingUp,
      color: 'text-sky-600',
      bgColor: 'bg-sky-50',
      subtitle: outstandingPassthrough > 0
        ? `+ ${formatCurrency(outstandingPassthrough)} pass-throughs · ${stats.overdue_invoices || 0} overdue`
        : `${stats.overdue_invoices || 0} overdue`,
      tooltip: 'Consulting fees with status "sent". Pass-through expenses shown separately. Excludes drafts and paid invoices.',
      navigateTo: '/reports?tab=drilldowns&view=outstanding-by-project',
    },
    {
      id: 'available-to-invoice',
      title: 'Available to Invoice',
      value: formatCurrency(stats.available_to_invoice || 0),
      icon: FileText,
      color: 'text-slate-600',
      bgColor: 'bg-slate-100',
      tooltip: 'Sum of unbilled amounts from confirmed billable items on active projects. Excludes: proposed items, on-hold items, and items on completed/cancelled projects.',
      navigateTo: '/reports?tab=drilldowns&view=available-to-invoice-by-project',
    },
    {
      id: 'active-projects',
      title: 'Active Projects',
      value: stats.active_projects || 0,
      icon: Briefcase,
      color: 'text-slate-600',
      bgColor: 'bg-slate-100',
      subtitle: `${stats.proposal_count || 0} proposals, ${stats.in_permit_count || 0} in permit`,
      tooltip: 'Projects in workflow stages: proposal, in_permit, or in_progress. Excludes: completed, on_hold, and cancelled.',
      navigateTo: '/projects',  // Phase 9.5f: Removed status filter, show all projects
    },
    {
      id: 'invoices',
      title: 'Invoices',
      value: stats.sent_invoices || 0,
      icon: FileText,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      subtitle: `${stats.draft_invoices || 0} drafts`,
      tooltip: 'Count of invoices with status "sent" awaiting payment. Draft count shown below.',
      navigateTo: '/invoices?status=sent',
    },
  ];

  const handleCardClick = (navigateTo: string) => {
    navigate(navigateTo);
  };

  return (
    <TooltipProvider>
      <div data-testid="dashboard-stats-cards" className="contents">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Tooltip key={stat.id}>
            <TooltipTrigger asChild>
              <div
                data-testid={`stat-card-${stat.id}`}
                onClick={() => handleCardClick(stat.navigateTo)}
                className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer hover:bg-gray-50"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCardClick(stat.navigateTo);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-medium text-gray-600">{stat.title}</h3>
                    <Info
                      size={14}
                      className="text-gray-400"
                      data-testid={`tooltip-${stat.id}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className={`${stat.bgColor} p-2 rounded-lg`}>
                    <Icon className={stat.color} size={20} />
                  </div>
                </div>
                <div className={`text-2xl font-bold ${stat.color}`} data-testid={`stat-${index}-value`}>
                  {stat.value}
                </div>
                {stat.subtitle && (
                  <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p>{stat.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
      </div>
    </TooltipProvider>
  );
}
